import ApiService from './api'

export const USER_PROFILE_OPTIONS = [
  { value: 'ADMIN', label: 'Administrador', badgeClass: 'profile-admin' },
  { value: 'LAWYER', label: 'Advogado', badgeClass: 'profile-lawyer' },
  { value: 'ASSISTANT', label: 'Secretário', badgeClass: 'profile-assistant' },
]

export const USER_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Ativo', badgeClass: 'status-active' },
  { value: 'INACTIVE', label: 'Inativo', badgeClass: 'status-inactive' },
  { value: 'PENDING_INVITE', label: 'Convite pendente', badgeClass: 'status-pending' },
]

export const PROFILE_PERMISSIONS = {
  ADMIN: ['Gerenciar usuários', 'Visualizar financeiro', 'Editar regras', 'Aprovar ações críticas'],
  LAWYER: ['Gerenciar casos', 'Atualizar prazos', 'Anexar documentos', 'Consultar dashboard'],
  ASSISTANT: ['Cadastrar informações', 'Atualizar tarefas', 'Consultar usuários', 'Gerar relatórios operacionais'],
  FINANCIAL: ['Visualizar faturamento', 'Exportar relatórios', 'Gerenciar cobranças', 'Consultar contratos'],
  READONLY: ['Somente leitura', 'Consultar casos', 'Visualizar indicadores'],
}

const PROFILE_MAP = {
  admin: 'ADMIN',
  administrador: 'ADMIN',
  superadmin: 'ADMIN',
  super_admin: 'ADMIN',
  lawyer: 'LAWYER',
  advogado: 'LAWYER',
  assistant: 'ASSISTANT',
  assistente: 'ASSISTANT',
  secretary: 'ASSISTANT',
  secretario: 'ASSISTANT',
  secretarioa: 'ASSISTANT',
  secretrio: 'ASSISTANT',
  financial: 'FINANCIAL',
  financeiro: 'FINANCIAL',
  readonly: 'READONLY',
  leitura: 'READONLY',
  viewer: 'READONLY',
}

const STATUS_MAP = {
  active: 'ACTIVE',
  ativo: 'ACTIVE',
  inactive: 'INACTIVE',
  inativo: 'INACTIVE',
  pending_invite: 'PENDING_INVITE',
  pendinginvite: 'PENDING_INVITE',
  convitependente: 'PENDING_INVITE',
  pending: 'PENDING_INVITE',
}

const PROFILE_TO_API_ROLE = {
  ADMIN: 'ADMIN',
  LAWYER: 'LAWYER',
  ASSISTANT: 'SECRETARY',
  FINANCIAL: 'FINANCIAL',
  READONLY: 'LAWYER',
}

function normalizeToken(value) {
  return String(value)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z_]/g, '')
}

function normalizeProfile(value) {
  if (!value) return 'READONLY'
  const normalized = normalizeToken(value)
  return PROFILE_MAP[normalized] || 'READONLY'
}

function normalizeStatus(user) {
  const explicitStatus = user.status || user.userStatus || user.situacao
  if (explicitStatus) {
    const normalized = normalizeToken(explicitStatus)
    return STATUS_MAP[normalized] || 'ACTIVE'
  }

  if (user.pendingInvite === true || user.convitePendente === true || user.invitePending === true) {
    return 'PENDING_INVITE'
  }

  if (user.ativo === false || user.active === false || user.inativo === true) {
    return 'INACTIVE'
  }

  return 'ACTIVE'
}

function normalizeRecentActions(user) {
  const history = user.recentActions || user.historico || user.historicoAcoes || user.auditTrail || []
  if (!Array.isArray(history)) return []

  return history.map((item, index) => ({
    id: item.id || item.codigo || `${user.id || 'user'}_action_${index}`,
    action: item.action || item.acao || item.title || 'Atualização',
    description: item.description || item.descricao || item.details || 'Registro retornado pela API.',
    timestamp: item.timestamp || item.data || item.createdAt || item.created_at || new Date().toISOString(),
  }))
}

function normalizePermissions(user, profile) {
  const permissions = user.permissions || user.permissoes
  if (Array.isArray(permissions) && permissions.length > 0) {
    return permissions.map((item) => item.label || item.nome || item.name || String(item))
  }

  return PROFILE_PERMISSIONS[profile] || []
}

export function mapApiUser(user) {
  const roleId = user.roleId || user.role_id || null
  const builtInProfiles = { 1: 'ADMIN', 2: 'LAWYER', 3: 'ASSISTANT', 4: 'ADMIN' }
  const profile = builtInProfiles[Number(roleId)] || user.role || user.perfil || user.profile || 'READONLY'
  const status = normalizeStatus(user)

  return {
    id: user.id || user.usuarioId || user._id,
    fullName: user.nome || user.fullName || user.name || 'Usuário sem nome',
    email: user.email || '',
    profile,
    roleId,
    status,
    phone: user.telefone || user.phone || '',
    roleTitle: user.cargo || user.roleTitle || user.titulo || '',
    createdAt: user.createdAt || user.created_at || user.dataCriacao || user.data_criacao || new Date().toISOString(),
    lastAccessAt: user.lastAccessAt || user.last_access_at || user.ultimoLogin || user.ultimo_login || user.ultimoAcesso || user.ultimo_acesso || null,
    permissions: normalizePermissions(user, profile),
    recentActions: normalizeRecentActions(user),
    inviteByEmail: Boolean(user.inviteByEmail ?? user.enviarConviteEmail ?? user.enviar_convite_email),
    raw: user,
  }
}

function serializeUserForm(data) {
  const apiRole = PROFILE_TO_API_ROLE[data.profile] || String(data.profile || 'LAWYER')

  return {
    nome: data.fullName.trim(),
    email: data.email.trim().toLowerCase(),
    role: apiRole,
    perfil: apiRole,
    ativo: data.status === 'ACTIVE',
    status: data.status,
    telefone: data.phone?.trim() || null,
    cargo: data.roleTitle?.trim() || null,
    senha: data.password || undefined,
    confirmarSenha: data.confirmPassword || undefined,
    enviarConviteEmail: Boolean(data.inviteByEmail),
  }
}

function ensureSuccess(result, defaultMessage) {
  if (!result.success) {
    throw new Error(result.error || defaultMessage)
  }

  return result
}

export async function listUsers() {
  const result = await ApiService.getUsuarios(200, 1)
  ensureSuccess(result, 'Não foi possível carregar os usuários.')
  return (result.items || []).map(mapApiUser)
}

export function filterUsers(users, filters) {
  const search = filters.search.trim().toLowerCase()
  const sorters = {
    name: (a, b) => a.fullName.localeCompare(b.fullName, 'pt-BR'),
    createdAt: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    lastAccessAt: (a, b) => new Date(b.lastAccessAt || 0) - new Date(a.lastAccessAt || 0),
  }

  return [...users]
    .filter((user) => {
      const matchesSearch = !search || user.fullName.toLowerCase().includes(search) || user.email.toLowerCase().includes(search)
      const matchesStatus = filters.status === 'ALL' || user.status === filters.status
      const matchesProfile = filters.profile === 'ALL' || user.profile === filters.profile
      return matchesSearch && matchesStatus && matchesProfile
    })
    .sort(sorters[filters.sortBy])
}

export function paginateUsers(users, filters) {
  const filtered = filterUsers(users, filters)

  const total = filtered.length
  const totalPages = Math.max(1, Math.ceil(total / filters.pageSize))
  const normalizedPage = Math.min(filters.page, totalPages)
  const start = (normalizedPage - 1) * filters.pageSize

  return {
    data: filtered.slice(start, start + filters.pageSize),
    total,
    totalPages,
    page: normalizedPage,
  }
}

export async function createUser(payload) {
  const result = await ApiService.createUsuario(serializeUserForm(payload))
  ensureSuccess(result, 'Não foi possível criar o usuário.')
  return mapApiUser(result.data || result.items || payload)
}

export async function updateUser(userId, payload) {
  const result = await ApiService.updateUsuario(userId, serializeUserForm(payload))
  ensureSuccess(result, 'Não foi possível atualizar o usuário.')
  return mapApiUser(result.data || payload)
}

export async function getUserDetails(userId) {
  const result = await ApiService.getUsuarioById(userId)
  ensureSuccess(result, 'Não foi possível carregar os detalhes do usuário.')
  return mapApiUser(result.data || result.items)
}

async function tryUserAction(userId, attempts, fallbackMessage) {
  for (const attempt of attempts) {
    const result = await attempt()
    if (result.success) return result
  }
  throw new Error(fallbackMessage)
}

export async function toggleUserStatus(user) {
  const nextStatus = user.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
  const payload = {
    ...serializeUserForm(user),
    ativo: nextStatus === 'ACTIVE',
    status: nextStatus,
  }

  const result = await ApiService.patchUsuario(user.id, payload)
  if (result.success) return mapApiUser(result.data || { ...user.raw, ...payload })

  const updateResult = await ApiService.updateUsuario(user.id, payload)
  ensureSuccess(updateResult, 'Não foi possível atualizar o status do usuário.')
  return mapApiUser(updateResult.data || { ...user.raw, ...payload })
}

export async function resetUserPassword(userId) {
  const result = await tryUserAction(
    userId,
    [
      () => ApiService.resetUsuarioPassword(userId),
      () => ApiService.postUsuarioAction(userId, 'resetar-senha'),
      () => ApiService.postUsuarioAction(userId, 'reset-senha'),
    ],
    'Não foi possível solicitar o reset de senha.',
  )

  return {
    success: true,
    message: result.data?.message || result.data?.detail || 'Reset de senha solicitado com sucesso.',
  }
}

export async function deleteUser(userId) {
  const result = await ApiService.deleteUsuario(userId)
  ensureSuccess(result, 'Não foi possível excluir o usuário.')
  return { success: true }
}

export function getProfileMeta(profile) {
  if (String(profile).toUpperCase() === 'ADMIN') return { value: 'ADMIN', label: 'Administrador', badgeClass: 'profile-admin' }
  if (String(profile).toUpperCase() === 'LAWYER') return { value: 'LAWYER', label: 'Advogado', badgeClass: 'profile-lawyer' }
  return USER_PROFILE_OPTIONS.find((option) => option.value === profile) || {
    value: profile || 'READONLY',
    label: profile || 'Não informado',
    badgeClass: 'profile-readonly',
  }
}

export async function listRoles() {
  const result = await ApiService.getPerfis()
  ensureSuccess(result, 'Não foi possível carregar os perfis.')
  return result.data || result.items || []
}

export function getStatusMeta(status) {
  return USER_STATUS_OPTIONS.find((option) => option.value === status)
}
